import { useEffect } from "react";
import { X, UserPlus, Trash2 } from "lucide-react";
import { useDocuments } from "../modal/DocumentsContext";
import MultiAssignDropdown from "shared/MultiAssignDropdown";
import type { SharePermission } from "../api/DocumentsApi";

interface Props {
  onClose: () => void;
}

export default function ShareDocumentModal({ onClose }: Props) {
  const {
    currentDocument, shares, assignableEmployees,
    loadShares, loadAssignableEmployees, addShares, editSharePermission, removeShare,
  } = useDocuments();

  useEffect(() => {
    loadShares();
    loadAssignableEmployees();
    // one modal instance is scoped to the document already loaded into context for its whole lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sharedIds = new Set(shares.map((s) => s.employee_id));
  const available = assignableEmployees.filter((e) => !sharedIds.has(e.id));

  async function handleConfirm(employeeIds: number[]) {
    await addShares(employeeIds, "view");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Share "{currentDocument?.title}"</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {shares.length > 0 && (
          <div className="mb-5">
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-2">Currently shared with</label>
            <div className="space-y-1.5">
              {shares.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-surface-muted/60 border border-border/60 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-foreground font-inter">{s.employee_name} ({s.employee_code})</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={s.permission}
                      onChange={(e) => editSharePermission(s.id, e.target.value as SharePermission)}
                      className="bg-surface border border-border rounded-lg text-xs text-foreground font-inter px-2 py-1 focus:outline-none focus:border-primary"
                    >
                      <option value="view">View</option>
                      <option value="edit">Edit</option>
                    </select>
                    <button onClick={() => removeShare(s.id)} className="text-destructive hover:text-destructive/80">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="block text-xs font-medium text-muted-foreground font-inter mb-2">Share with</label>
        {available.length === 0 ? (
          <p className="text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-5">
            {assignableEmployees.length === 0
              ? "No employees are eligible to be shared with yet."
              : "All eligible employees already have access."}
          </p>
        ) : (
          <div className="mb-5">
            <MultiAssignDropdown
              options={available.map((e) => ({ id: e.id, label: `${e.name} (${e.employee_code})` }))}
              onConfirm={handleConfirm}
              placeholder="Select employees…"
              confirmLabel={(n) => (n > 0 ? `Share with ${n}` : "Share")}
            />
            <p className="text-[11px] text-muted-foreground font-inter mt-1.5">New shares start at "View" — change to "Edit" above once added.</p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
